export const MARKET_PALETTE = Object.freeze({
  growthPeak: "#45E4F5", growth: "#20A5BB", growthModerate: "#136C7D", growthLow: "#034859",
  warmWhite: "#E9E0D5", stone: "#C7BFB2", warmGray: "#A7A095", slate: "#777D7B",
  softCoral: "#E98978", coral: "#F4564D", deepCoral: "#C53C35", declineWorst: "#9F3938",
  raised: "#242A2D", panel: "#14191B", canvas: "#0B0F11", text: "#F4F0E9",
});

export const MARKET_GROWTH_BANDS = Object.freeze([
  { max: -.5, color: MARKET_PALETTE.declineWorst },
  { max: -.15, color: MARKET_PALETTE.deepCoral },
  { max: -.05, color: MARKET_PALETTE.coral },
  { max: -.02, color: MARKET_PALETTE.softCoral },
  { max: -.01, color: MARKET_PALETTE.slate },
  { max: -.005, color: MARKET_PALETTE.warmGray },
  { max: .005, color: MARKET_PALETTE.stone },
  { max: .02, color: MARKET_PALETTE.warmWhite },
  { max: .15, color: MARKET_PALETTE.growthLow },
  { max: .5, color: MARKET_PALETTE.growthModerate },
  { max: 1.5, color: MARKET_PALETTE.growth },
  { max: Infinity, color: MARKET_PALETTE.growthPeak },
]);

export function marketGrowthColor(growth) {
  const value = Number.isFinite(growth) ? growth : 0;
  return MARKET_GROWTH_BANDS.find(band => value <= band.max).color;
}

export function marketTotalColor(value, maximum) {
  const colors = [
    MARKET_PALETTE.declineWorst, MARKET_PALETTE.coral, MARKET_PALETTE.softCoral,
    MARKET_PALETTE.warmWhite, MARKET_PALETTE.stone, MARKET_PALETTE.growthLow,
    MARKET_PALETTE.growthModerate, MARKET_PALETTE.growth, MARKET_PALETTE.growthPeak,
  ];
  const level = maximum > 0 ? Math.log1p(Math.max(0, value)) / Math.log1p(maximum) : 0;
  return colors[Math.min(colors.length - 1, Math.floor(level * colors.length))];
}

// Rank-based total color: spreads the palette evenly across the actual
// distribution so a few huge outliers (e.g. TikTok's trillion-view games)
// don't compress everything else into the middle blue bands. `sortedValues`
// is the ascending list of all values in the population.
export function marketTotalRankColor(value, sortedValues) {
  const colors = [
    MARKET_PALETTE.declineWorst, MARKET_PALETTE.coral, MARKET_PALETTE.softCoral,
    MARKET_PALETTE.warmWhite, MARKET_PALETTE.stone, MARKET_PALETTE.growthLow,
    MARKET_PALETTE.growthModerate, MARKET_PALETTE.growth, MARKET_PALETTE.growthPeak,
  ];
  if (!Array.isArray(sortedValues) || !sortedValues.length) return colors[0];
  // binary search for the rank of value within sortedValues
  let lo = 0, hi = sortedValues.length - 1, idx = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (sortedValues[mid] <= value) { idx = mid; lo = mid + 1; } else hi = mid - 1;
  }
  const pct = sortedValues.length > 1 ? idx / (sortedValues.length - 1) : 1;
  return colors[Math.min(colors.length - 1, Math.floor(pct * colors.length))];
}

export function marketLabelColor(color) {
  return [MARKET_PALETTE.warmWhite, MARKET_PALETTE.stone, MARKET_PALETTE.warmGray, MARKET_PALETTE.slate, MARKET_PALETTE.softCoral, MARKET_PALETTE.coral].includes(color)
    ? MARKET_PALETTE.canvas : MARKET_PALETTE.text;
}

export function marketLabelStroke(color) {
  // Blue/growth fills are mid-to-bright, so light labels need a stronger dark
  // halo to stay legible (especially the brightest cyan peak).
  const isGrowth = [MARKET_PALETTE.growthPeak, MARKET_PALETTE.growth, MARKET_PALETTE.growthModerate, MARKET_PALETTE.growthLow].includes(color);
  if (isGrowth) return "rgba(11,15,17,0.92)";
  return marketLabelColor(color) === MARKET_PALETTE.text ? "rgba(11,15,17,0.65)" : "none";
}

export const MARKET_FAVORITES = [
  { value: "upcoming", label: "Upcoming Games" },
  { value: "recent", label: "Recent Releases" },
  { value: "live-service", label: "Live Service Games" },
  { value: "free-to-play", label: "Free to Play" },
];

export function matchesMarketFavorite(game, favorite, today = new Date()) {
  const normalize = value => String(value || "").trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");
  if (favorite === "live-service") return normalize(game.live_service_static) === "live service";
  if (favorite === "free-to-play") return normalize(game.pricing) === "free to play";
  if (!game.release_date) return false;
  // ISO date-only values must not shift into yesterday in timezones west of UTC.
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(game.release_date);
  const release = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : new Date(game.release_date);
  if (!Number.isFinite(release.getTime())) return false;
  const day = date => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000;
  const age = day(today) - day(release);
  if (favorite === "upcoming") return age < 0;
  if (favorite === "recent") return age >= 0 && age < 30;
  return false;
}
